import React, { useState, useEffect } from "react";
import MainLayout from "../components/layout/MainLayout";
import { useAuth } from "../contexts/AuthContext";
import api, { BASE_URL } from "../services/api";
import Swal from "sweetalert2";
import "./Profile.css";
import { useNavigate } from "react-router-dom";

const formatMoney = (amount) =>
  `Rs. ${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

function Profile() {
  const { updateUser } = useAuth();
  const navigate = useNavigate(); // ← add this

  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    profile_photo_path: null,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get("/profile");
      if (res.data.success) {
        setUserDetails(res.data.data);
        setFormData({
          first_name: res.data.data.first_name || "",
          last_name: res.data.data.last_name || "",
          phone_number: res.data.data.phone_number || "",
          profile_photo_path: null,
        });
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load profile data",
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Avatar helper ────────────────────────────────────────────────────
  const getAvatar = (firstName, lastName, photoPath) => {
    if (photoPath) {
      if (photoPath.startsWith("http")) return photoPath;
      if (photoPath.startsWith("/")) return `${BASE_URL}${photoPath}`;
      return `${BASE_URL}/upload/User/${photoPath}`;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      (firstName || "U") + " " + (lastName || "ser"),
    )}&background=6557ff&color=fff&size=128`;
  };

  // ── Form handlers ────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormData((prev) => ({ ...prev, profile_photo_path: file }));
    const reader = new FileReader();
    reader.onloadend = () => setPreviewImage(reader.result);
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim())
      newErrors.first_name = "First name is required";
    if (!formData.last_name.trim())
      newErrors.last_name = "Last name is required";
    if (!formData.phone_number.trim())
      newErrors.phone_number = "Phone number is required";
    else if (formData.phone_number.length !== 10)
      newErrors.phone_number = "Phone number must be 10 digits";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("first_name", formData.first_name);
      fd.append("last_name", formData.last_name);
      fd.append("phone_number", formData.phone_number);
      if (formData.profile_photo_path)
        fd.append("profile_photo_path", formData.profile_photo_path);

      const res = await api.post("/update-user-profile", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: res.data.message,
          timer: 2000,
          showConfirmButton: false,
        });
        setShowEditModal(false);
        setPreviewImage(null);
        fetchProfile();
        if (updateUser) updateUser(res.data.data);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to update profile",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Invoice download ─────────────────────────────────────────────────
  const handleDownloadInvoice = async (planId) => {
    try {
      const res = await api.get(`/profile/invoice/${planId}`, {
        responseType: "blob",
      });
      const disposition =
        res.headers?.["content-disposition"] ||
        res.headers?.["Content-Disposition"] ||
        "";
      const fileNameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const downloadName = fileNameMatch?.[1] || `Invoice-${planId}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to download invoice",
      });
    }
  };

  if (loading)
    return (
      <MainLayout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </MainLayout>
    );

  if (!userDetails)
    return (
      <MainLayout>
        <div className="error-container">
          <p>Failed to load profile data</p>
        </div>
      </MainLayout>
    );

  const isCompanyAdmin = userDetails.designation_name === "Company Admin";

  return (
    <MainLayout>
      <div className="profile-page">
        <div className="profile-card">
          <div className="profile-content">
            {/* ── Left: Avatar + Name ──────────────────────────── */}
            <div className="profile-avatar-section">
              <div className="avatar-wrapper">
                <img
                  src={getAvatar(
                    userDetails.first_name,
                    userDetails.last_name,
                    `/upload/User/${userDetails.profile_photo_path}`,
                  )}
                  alt="Profile"
                  className="profile-avatar"
                  onError={(e) => {
                    // Fallback if image 404s
                    e.target.src = getAvatar(
                      userDetails.first_name,
                      userDetails.last_name,
                      null,
                    );
                  }}
                />
              </div>
              <div className="profile-name-section">
                {/* Company name on top */}
                {userDetails.company_name && (
                  <p className="company-name-badge">
                    <i className="fa-solid fa-building me-1"></i>
                    {userDetails.company_name}
                  </p>
                )}
                <h5>
                  {userDetails.first_name} {userDetails.last_name}
                </h5>
                {/* Designation removed as per requirement */}
              </div>
            </div>

            {/* ── Middle: Profile Info ─────────────────────────── */}
            <div className="profile-info-section">
              <h6>Profile Information</h6>

              <ul className="info-list">
                <li>
                  <strong>Name:</strong>&nbsp;
                  {userDetails.first_name} {userDetails.middle_name}{" "}
                  {userDetails.last_name}
                </li>
                <li className="info-row">
                  <div>
                    <strong>Mobile:</strong> {userDetails.phone_number || "—"}
                  </div>
                  <div>
                    <strong>Email:</strong> {userDetails.email}
                  </div>
                </li>
                <li className="info-row">
                  <div>
                    <strong>Company:</strong>{" "}
                    {userDetails.company_name || "Cedur"}
                  </div>
                  <div>
                    {/* GST added, designation removed */}
                    <strong>GST No:</strong>&nbsp;
                    {userDetails.company_gst || "—"}
                  </div>
                </li>
                {userDetails.company_pan && (
                  <li>
                    <strong>PAN:</strong> {userDetails.company_pan}
                  </li>
                )}
                {userDetails.company_address && (
                  <li>
                    <strong>Address:</strong> {userDetails.company_address}
                  </li>
                )}

                {/* ── Active Plan (Company Admin only) ───────── */}
                {isCompanyAdmin && userDetails.active_plan && (
                  <li className="plan-section">
                    <strong>Current Plan</strong>
                    <div className="plan-card">
                      <div className="plan-row">
                        <div>
                          <strong>Plan Name:</strong>&nbsp;
                          {userDetails.active_plan.plan_name}
                        </div>
                        <div>
                          <strong>Details:</strong>&nbsp;
                          {userDetails.active_plan.plan_details}
                        </div>
                      </div>
                      <div className="plan-row">
                        <div>
                          <strong>Validity:</strong>&nbsp;
                          {userDetails.active_plan.plan_validity}
                        </div>
                        <div>
                          <strong>Current Estimate:</strong>&nbsp;
                          {formatMoney(userDetails.active_plan.estimated_amount)}
                        </div>
                      </div>
                      <div className="plan-row">
                        <div>
                          <strong>Included Employees:</strong>&nbsp;
                          {userDetails.active_plan.included_employees ?? "—"}
                        </div>
                        <div>
                          <strong>Employee Onboarding:</strong>&nbsp;
                          Unlimited
                        </div>
                      </div>
                      <div className="plan-row">
                        <div>
                          <strong>Current Employees:</strong>&nbsp;
                          {userDetails.active_plan.current_employee_count ?? "—"}
                        </div>
                        <div>
                          <strong>Peak Employees:</strong>&nbsp;
                          {userDetails.active_plan.peak_employee_count ?? "—"}
                        </div>
                      </div>
                      <div className="plan-row">
                        <div>
                          <strong>Add-on Rate:</strong>&nbsp;
                          {formatMoney(userDetails.active_plan.add_on_rate)}
                        </div>
                        <div>
                          <strong>Additional Employees:</strong>&nbsp;
                          {userDetails.active_plan.additional_employee_count ?? "—"}
                        </div>
                      </div>
                      {(userDetails.active_plan.start_date ||
                        userDetails.active_plan.end_date) && (
                        <div className="plan-row">
                          <div>
                            <strong>Start:</strong>&nbsp;
                            {userDetails.active_plan.start_date
                              ? new Date(
                                  userDetails.active_plan.start_date,
                                ).toLocaleDateString("en-IN")
                              : "—"}
                          </div>
                          <div>
                            <strong>Expires:</strong>&nbsp;
                            {userDetails.active_plan.end_date
                              ? new Date(
                                  userDetails.active_plan.end_date,
                                ).toLocaleDateString("en-IN")
                              : "—"}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="plan-actions">
                      <button
                        className="btn-upgrade"
                        onClick={() => navigate("/upgrade")}
                      >
                        <i className="fa-solid fa-arrow-up me-1"></i>
                        Upgrade Plan
                      </button>
                      <button
                        className="btn-history"
                        onClick={() => navigate("/plan-history")}
                      >
                        <i className="fa-solid fa-clock-rotate-left me-1"></i>
                        Billing History
                      </button>
                    </div>
                  </li>
                )}
              </ul>
            </div>

            {/* ── Right: Edit Button ───────────────────────────── */}
            <div className="profile-actions">
              <button
                className="btn-edit"
                onClick={() => {
                  setFormData({
                    first_name: userDetails.first_name || "",
                    last_name: userDetails.last_name || "",
                    phone_number: userDetails.phone_number || "",
                    profile_photo_path: null,
                  });
                  setPreviewImage(null);
                  setErrors({});
                  setShowEditModal(true);
                }}
              >
                <i className="fa-solid fa-user-edit"></i>
                <span>Edit</span>
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* ── Edit Profile Modal ──────────────────────────────────────── */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>Edit Profile</h5>
              <button
                className="modal-close"
                onClick={() => setShowEditModal(false)}
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-group">
                <label>
                  First Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  className="form-control"
                  value={formData.first_name}
                  onChange={handleInputChange}
                />
                {errors.first_name && (
                  <span className="error-text">{errors.first_name}</span>
                )}
              </div>
              <div className="form-group">
                <label>
                  Last Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  className="form-control"
                  value={formData.last_name}
                  onChange={handleInputChange}
                />
                {errors.last_name && (
                  <span className="error-text">{errors.last_name}</span>
                )}
              </div>
              <div className="form-group">
                <label>
                  Mobile <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="phone_number"
                  className="form-control"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  maxLength="10"
                />
                {errors.phone_number && (
                  <span className="error-text">{errors.phone_number}</span>
                )}
              </div>
              <div className="form-group">
                <label>Profile Image</label>
                <div className="image-upload-row">
                  <div className="current-image">
                    <img
                      src={
                        previewImage ||
                        getAvatar(
                          formData.first_name,
                          formData.last_name,
                          userDetails.profile_photo_path,
                        )
                      }
                      alt="Preview"
                      onError={(e) => {
                        e.target.src = getAvatar(
                          formData.first_name,
                          formData.last_name,
                          null,
                        );
                      }}
                    />
                  </div>
                  <input
                    type="file"
                    name="profile_photo_path"
                    className="form-control"
                    onChange={handleFileChange}
                    accept="image/jpeg,image/png,image/gif"
                  />
                  <small className="profile-image-note">Accepted: JPG, JPEG, PNG or GIF. Maximum size 2 MB.</small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={submitting}
                >
                  {submitting ? "Updating..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Billing History Modal ──────────────────────────────────── */}
      {showBillingModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowBillingModal(false)}
        >
          <div
            className="modal-container modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h5>
                <i className="fa-solid fa-file-invoice-dollar me-2"></i>
                Billing History
              </h5>
              <button
                className="modal-close"
                onClick={() => setShowBillingModal(false)}
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {!userDetails.billing_history?.length ? (
                <div className="billing-empty">
                  <i className="fa-solid fa-receipt"></i>
                  <p>No billing history found</p>
                </div>
              ) : (
                <table className="billing-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Plan</th>
                      <th>Amount</th>
                      <th>Validity</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Status</th>
                      <th>Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userDetails.billing_history.map((bill, i) => (
                      <tr key={bill.id}>
                        <td>{i + 1}</td>
                        <td>{bill.plan_name}</td>
                        <td>
                          {bill.amount
                            ? `₹${Number(bill.amount).toLocaleString("en-IN")}`
                            : "—"}
                        </td>
                        <td>{bill.validity || "—"}</td>
                        <td>
                          {bill.start_date
                            ? new Date(bill.start_date).toLocaleDateString(
                                "en-IN",
                              )
                            : "—"}
                        </td>
                        <td>
                          {bill.end_date
                            ? new Date(bill.end_date).toLocaleDateString(
                                "en-IN",
                              )
                            : "—"}
                        </td>
                        <td>
                          <span
                            className={`status-badge ${
                              bill.status === "Active" ? "active" : "expired"
                            }`}
                          >
                            {bill.status}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn-download-invoice"
                            onClick={() => handleDownloadInvoice(bill.id)}
                            title="Download Invoice"
                          >
                            <i className="fa-solid fa-download"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

    </MainLayout>
  );
}

export default Profile;
